import { useFormContext } from 'react-hook-form';
import styles from './styles.module.scss';
import {
  ResendCodeButton,
  ValidateCodeInput,
} from '@starsoft/common/components';
import { UserPhoneNumberVerifyInputDto } from '@starsoft/common/dto';
import { useSendUserPhoneNumberCode } from '../../../../../../api';

/**
 * UserPhoneNumberFormVerify Component
 * This component is responsible for rendering the verification form for user phone numbers.
 * It utilizes form context to manage form state and provides functionality to resend verification codes.
 *
 * @param {Object} props - The properties for the component.
 * @param {boolean} [props.isContainerColor] - Optional flag to determine if the container should have a color.
 *
 * @returns {JSX.Element} The rendered user phone number verification form component.
 *
 * @example
 * // Usage example:
 * <UserPhoneNumberFormVerify isContainerColor={true} />
 */
export default function UserPhoneNumberFormVerify({
  isContainerColor,
}: {
  isContainerColor?: boolean;
}) {
  // Access form context to manage form state
  const { setValue, watch } = useFormContext<UserPhoneNumberVerifyInputDto>();
  // Watch for changes in the 'phoneNumberId' field
  const phoneNumberId: string = watch('phoneNumberId');

  // Destructure necessary values and functions from the custom hook
  const { isPending, isSuccess, sendUserPhoneNumberCode } =
    useSendUserPhoneNumberCode();

  /**
   * resendPhoneNumberCode Function
   * This function triggers the resend of the verification code for the phone number.
   * It uses the phoneNumberId from the form context to send the code.
   */
  function resendPhoneNumberCode() {
    sendUserPhoneNumberCode({
      phoneNumberId: phoneNumberId?.toString(),
    });
  }

  /**
   * onChange Function
   * This function updates the 'code' field in the form context with the provided value.
   *
   * @param {string} value - The new value for the 'code' field.
   *
   * @example
   * // Example usage:
   * onChange('123456');
   */
  function onChange(value: string) {
    setValue('code', value);
  }

  // Render the verification form with input and resend button
  return (
    <div className={styles.container}>
      <ValidateCodeInput
        setValue={onChange}
        isContainerColor={isContainerColor}
      />
      <ResendCodeButton
        onClick={resendPhoneNumberCode}
        isLoading={isPending}
        isSuccess={isSuccess}
      />
    </div>
  );
}
